import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, ChevronRight, ChevronLeft, FileText, ClipboardList, Percent, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgStatTile } from '@/features/OrgLayout/primitives/OrgStatTile';
import { OrgProgressBar } from '@/features/OrgLayout/primitives/OrgProgressBar';
import { EmptyState } from '@/components/EmptyState';
import { useOrgBatchAssignments } from '@/lib/hooks/useOrg';
import type { OrgBatchAssignmentRow } from '@/lib/api/org';

const PAGE_SIZE = 10;

function KindBadge({ kind }: { kind: OrgBatchAssignmentRow['kind'] }) {
  const isProject = kind === 'project';
  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
      isProject ? 'bg-[#eff6ff] text-[#2563eb]' : 'bg-[#f3f4f6] text-[#6b7280]',
    )}>
      {isProject ? 'Project' : 'Assignment'}
    </span>
  );
}

export function BatchAssignmentsTab({ batchId }: { batchId: string }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useOrgBatchAssignments(batchId);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const assignments = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assignments;
    return assignments.filter((a) => a.title.toLowerCase().includes(q));
  }, [assignments, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goToAssignment = (assignmentId: string) =>
    navigate({ to: '/org/batches/$batchId/assignments/$assignmentId', params: { batchId, assignmentId } });

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-24 rounded-[24px]" />
        <Skeleton className="h-64 rounded-[24px]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <p className="text-[#6b7280]">Couldn&apos;t load assignments.</p>
        <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No assignments yet"
        description="Assignments added to this batch's curriculum will show up here."
      />
    );
  }

  const avgSubmissionRate = Math.round(
    assignments.reduce((sum, a) => sum + (a.total_students ? (a.submitted_count / a.total_students) * 100 : 0), 0)
      / assignments.length,
  );
  const avgScore = Math.round(
    assignments.reduce((sum, a) => sum + (a.max_score ? (a.avg_score / a.max_score) * 100 : 0), 0)
      / assignments.length,
  );
  const overdueCount = assignments.filter((a) => a.overdue).length;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-[#111827]">Assignments</h2>
        <p className="text-sm text-[#6b7280]">Submission status for this batch&apos;s assignments and projects.</p>
      </div>

      <OrgCard className="flex overflow-hidden">
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={FileText} iconBg="#fff4f0" iconColor="#ff5a1f" value={assignments.length} label="Total Assignments" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={ClipboardList} iconBg="#eff6ff" iconColor="#2563eb" value={`${avgSubmissionRate}%`} label="Avg Submission Rate" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={Percent} iconBg="#f0fdfa" iconColor="#0f766e" value={`${avgScore}%`} label="Avg Score" /></div>
        <div className="flex-1"><OrgStatTile icon={AlertTriangle} iconBg="#fef2f2" iconColor="#dc2626" value={overdueCount} label="Overdue" /></div>
      </OrgCard>

      <div className="flex h-[42px] w-[320px] items-center gap-3 rounded-[14px] border border-[#ececec] bg-white px-[17px]">
        <Search className="h-[15px] w-[15px] flex-shrink-0 text-[#6b7280]" />
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search assignments..."
          className="w-full border-none bg-transparent text-sm text-[#111827] placeholder:text-[rgba(107,114,128,0.5)] focus:outline-none"
        />
      </div>

      <OrgCard className="overflow-hidden">
        {pageItems.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#9ca3af]">No assignments match your search.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-[#ececec]">
                  {['Title', 'Kind', 'Due date', 'Submitted', 'Avg score', ''].map((h, i) => (
                    <th key={i} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageItems.map((a, i) => (
                  <tr
                    key={a.assignment_id}
                    onClick={() => goToAssignment(a.assignment_id)}
                    className={cn('cursor-pointer hover:bg-[#fafafa]', i !== pageItems.length - 1 && 'border-b border-[#ececec]')}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-[#111827]">{a.title}</td>
                    <td className="px-6 py-4"><KindBadge kind={a.kind} /></td>
                    <td className="px-6 py-4 text-sm text-[#374151]">
                      {a.due_date || '—'}
                      {a.overdue && (
                        <span className="ml-2 rounded-full bg-[#fef2f2] px-2 py-0.5 text-xs font-medium text-[#dc2626]">Overdue</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <OrgProgressBar
                          pct={a.total_students ? (a.submitted_count / a.total_students) * 100 : 0}
                          className="w-[80px]"
                          barClassName="bg-[#0f766e]"
                        />
                        <span className="text-sm tabular-nums text-[#111827]">{a.submitted_count}/{a.total_students}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums text-[#374151]">{Math.round(a.max_score ? (a.avg_score / a.max_score) * 100 : 0)}%</td>
                    <td className="px-6 py-4"><ChevronRight className="h-4 w-4 text-[#9ca3af]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-[#ececec] px-6 py-4">
          <p className="text-sm text-[#9ca3af]">
            {filtered.length === 0 ? 'Showing 0 assignments' : `Showing ${from} to ${to} of ${filtered.length} assignments`}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Previous page" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:opacity-40">
              <ChevronLeft className="h-[14px] w-[14px]" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              return (
                <button key={n} type="button" onClick={() => setPage(n)} className={cn('flex h-[34px] w-[34px] items-center justify-center rounded-[14px] text-sm font-semibold', n === currentPage ? 'bg-[#111827] text-white' : 'border border-[#ececec] text-[#111827]')}>
                  {n}
                </button>
              );
            })}
            <button type="button" aria-label="Next page" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:opacity-40">
              <ChevronRight className="h-[14px] w-[14px]" />
            </button>
          </div>
        </div>
      </OrgCard>
    </div>
  );
}
